/* Birthday Automation — display-only constants (NO business logic / authority).
 * Status colors follow the existing Nexus soft-tint badge style (see CAT_COLORS).
 */

export const STATUS_META = {
  not_synced:                   { label: "Not synced",        bg: "#F4F2EE", color: "#9B9792" },
  draft:                        { label: "Draft",             bg: "#F4F2EE", color: "#6B6B6B" },
  missing_photo:                { label: "Missing photo",     bg: "#FFF3E0", color: "#E65100" },
  ready_for_preview:            { label: "Ready for preview", bg: "#E3F2FD", color: "#1565C0" },
  preview_generation_requested: { label: "Preview requested", bg: "#E3F2FD", color: "#1565C0" },
  preview_generated:            { label: "Preview ready",     bg: "#E8F5E9", color: "#2E7D32" },
  pending_approval:             { label: "Pending approval",  bg: "#FFF0ED", color: "#FF5A00" },
  approved:                     { label: "Approved",          bg: "#E8F5E9", color: "#15803D" },
  rejected:                     { label: "Rejected",          bg: "#FDECEA", color: "#DC2626" },
  skipped:                      { label: "Skipped",           bg: "#F4F2EE", color: "#6B6B6B" },
  duplicate_warning:            { label: "Duplicate warning", bg: "#FFF8E1", color: "#B7791F" },
  duplicate_blocked:            { label: "Duplicate blocked", bg: "#FDECEA", color: "#DC2626" },
  send_requested:               { label: "Send requested",    bg: "#E3F2FD", color: "#1565C0" },
  force_send_requested:         { label: "Force send req.",   bg: "#FFF8E1", color: "#B7791F" },
  sent:                         { label: "Sent",              bg: "#E8F5E9", color: "#15803D" },
  failed:                       { label: "Failed",            bg: "#FDECEA", color: "#DC2626" },
};

export function statusMeta(status) {
  return STATUS_META[status] || { label: status || "Unknown", bg: "#F4F2EE", color: "#6B6B6B" };
}

/* Friendly labels for the settings summary panel. */
export const SETTING_LABELS = {
  "birthday.automation_enabled": "Automation",
  "birthday.dry_run_enabled": "Dry-run",
  "birthday.n8n_integration_enabled": "n8n integration",
  "birthday.email_fallback_enabled": "Email fallback",
  "birthday.test_mode_enabled": "Test mode",
  "birthday.source_of_truth": "Source of truth",
  "birthday.default_template_id": "Default template",
};

/* ── Gated UI actions (Phase 10.2) ─────────────────────────────────────────
 * DISPLAY-ONLY. This mirrors the Phase 6 backend state machine purely so the UI
 * can hide/show buttons for a clean UX. It is NOT an authority: the backend
 * (api/_modules/birthday/stateMachine.js) re-validates every transition and
 * remains the single source of truth. Never send/force-send here.
 */
export const ACTIONS_BY_STATUS = {
  draft:                        ["skip"],
  missing_photo:                ["skip"],
  ready_for_preview:            ["mock_preview", "skip"],
  preview_generation_requested: ["skip"],
  preview_generated:            ["submit_for_approval", "skip"],
  pending_approval:             ["approve", "reject", "skip"],
  duplicate_warning:            ["approve", "reject", "skip"],
};

/* Per-action display metadata: button label, modal copy, trigger style, and
 * whether a reason is required/optional. No business logic lives here. */
export const ACTION_META = {
  mock_preview: {
    label: "Generate mock preview", kind: "secondary", tone: "info",
    title: "Generate mock preview", confirmLabel: "Generate preview",
    body: "This creates a mocked preview marker only — no image is rendered and nothing is stored. The run moves to “Preview ready”.",
  },
  submit_for_approval: {
    label: "Submit for approval", kind: "secondary", tone: "info",
    title: "Submit for approval", confirmLabel: "Submit",
    body: "This moves the run to “Pending approval”. No email is sent and nothing is queued for sending.",
  },
  approve: {
    label: "Approve", kind: "primary", tone: "success",
    title: "Approve birthday run", confirmLabel: "Approve",
    body: "This records an approval decision only. It does not send anything — sending is disabled in this phase.",
  },
  reject: {
    label: "Reject", kind: "danger", tone: "warn", reason: "optional",
    title: "Reject birthday run", confirmLabel: "Reject",
    body: "This rejects the birthday run. You can add an optional reason for the record.",
  },
  skip: {
    label: "Skip", kind: "secondary", tone: "warn", reason: "required",
    title: "Skip birthday run", confirmLabel: "Skip",
    body: "This skips the run for today. A reason is required and will be recorded.",
  },
};

export function actionsForStatus(status) {
  return ACTIONS_BY_STATUS[status] || [];
}

export const SUMMARY_CARDS = [
  { key: "total",             label: "Today's candidates" },
  { key: "missing_photo",     label: "Missing photos" },
  { key: "ready_for_preview", label: "Ready for preview" },
  { key: "pending_approval",  label: "Pending approval" },
  { key: "approved",          label: "Approved" },
  { key: "duplicate",         label: "Duplicate warnings" },
  { key: "dry_run_jobs",      label: "Dry-run jobs" },
];
